import * as Sentry from '@sentry/react';
import React, { useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal, TextField, InputAdornment, IconButton } from '@mui/material';
import { Assignment, Person, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { styled as muiStyled } from '@mui/material/styles';
import COLOR from 'utils/constants/color';
import CotationLoader from 'components/CotationLoader';
import Loading from 'components/Loading';
import queryString from 'query-string';
import styled from 'styled-components';
import { getCurrentUser } from 'aws-amplify/auth';
import { Col, Row } from 'antd';
import { getRandomCotation } from "utils/methods/math";
import { handleGetClass } from 'store/classes/thunks';
import { handleDeepLinkRedirect } from 'utils/handleDeepLinkRedirect';
import { handleLogin, handleRedirect } from 'store/auth/thunks';
import { lighten } from 'polished';
import { useFirebase } from 'containers/FirebaseContext';
import { SessionManager } from 'utils/lib/api_config';
import useQuotesData from 'components/hooks/useQuotesData';
import { handleOnceLogin, handleGoogleAuthResponse, useGoogleAuth, getStoredGoogleEmail } from 'components/auth_flow';
import { handleClasslinkAuth } from 'components/auth_flow/handleClasslinkAuth';
import { handleCanvasAuth } from 'components/auth_flow/handleCanvasAuth';
import CanvasLoginButton from 'components/CanvasLoginButton';
import { handleCleverAuth } from 'components/auth_flow/handleCleverAuth';
import { handleSchoologyAuth } from 'components/auth_flow/handleSchoologyAuth';
import TwoFactorAuthModal from 'components/TwoFactorAuthModal';
const local_store_keys = {
    search_item:"search_item",
    auth_info:"auth_info",
    log_id:"log_id",
    canvas_selected_district:"canvas_selected_district",
    canvas_auth_token:"canvas_auth_token"
}

const INTEGRATION_TEST_SITE_URL = 'https://dev.nosotrosonline.com';
const INTEGRATION_TEST_SITE_PATH = '/login';

const hasIntegrationParams = (search) => {
    if (!search || typeof search !== 'object' || Object.keys(search).length === 0) {
        return false;
    }
    return !!(
        search.code ||
        search.id_token ||
        search.lti_message_type ||
        search.custom_canvas_api_domain ||
        search.sjwt ||
        search.oauth_token ||
        search.key ||
        search.context_id ||
        search.lti_platform ||
        search.uid
    );
};

const Container = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-top: 0px;
    margin-bottom: 18px;
`;

const Description = styled.span`
    color: ${COLOR.black500};
    display: block;
    font-size: 12px;
    font-weight: 400;
`;

const Error = styled.div`
    background-color: ${lighten(0.45, 'red')};
    border: 1px solid ${lighten(0.2, 'red')};
    border-radius: 3px;
    color: red;
    font-size: 14px;
    font-weight: 500;
    padding: 8px;
    text-align: center;
    width: 100%;
`;
const AutoLoginMessage = styled.div`
    border-radius: 3px; 
    color: red;
    font-size: 14px;
    font-weight: 500;
    padding: 8px;
    text-align: center;
    width: 100%;
`

const Form = styled.form`
    align-items: center;
    display: flex; 
    flex-direction: column;
    margin-top: 24px;
`;

const Link = styled.button`
    background-color: transparent;
    border: none;
    color: ${COLOR.green100};
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    outline: none;
    transition: all 300ms ease;

    &:hover {
        text-decoration: underline;
    }
`;

const RegisterContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-top: 24px;
`;

const Text = styled.span`
    color: ${COLOR.black500};
    display: inline-block;
    font-size: 13px;
    margin-right: 3px;
`;

const Toggle = styled.button`
    background-color: transparent;
    border: none;
    border-bottom: 2px solid ${p => p.$active ? COLOR.green100 : COLOR.black800};
    color: ${p => p.$active ? COLOR.black200 : COLOR.black500};
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    outline: none;
    padding: 10px 0px; 
    padding-top: 0px;
    text-transform: uppercase;
    transition: all 300ms ease;
    width: 50%;

    &:hover {
        border-bottom: 2px solid ${COLOR.green700};
        color: ${COLOR.black400};
    }
`;

const Wrapper = styled.div`
    max-width: 400px;
    position: relative;
    width: 100%;
`;
const VideoContainer = styled.div`
    height: auto;
    flex: 1;
    justify-content: center;
`;



const GoogleClassroomButton = styled.button`
  background-color: #fff; /* Google Classroom color */
  color: black;
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  display: inline-block;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  transition: background-color 0.3s;
  font-family: 'Roboto', sans-serif;

  &::before {
    content: '';
    display: inline-block;
    width: 24px;
    height: 24px;
    background-image: url('https://firebasestorage.googleapis.com/v0/b/yo-americano.appspot.com/o/assets%2F96x96_yellow_stroke_icon%402x%20(1).png?alt=media&token=744575cd-08ce-4750-b0c1-117b2f1acdda');
    background-size: cover;
    margin-right: 8px;
    vertical-align: middle;
  }
`;

const StyledModal = muiStyled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledTextField = muiStyled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 100px #fff inset',
      WebkitTextFillColor: '#000',
      caretColor: '#000',
      borderRadius: '4px',
    },
    '& input:-webkit-autofill:hover': {
      WebkitBoxShadow: '0 0 0 100px #fff inset',
    },
    '& input:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 100px #fff inset',
    },
    '& input:-webkit-autofill:active': {
      WebkitBoxShadow: '0 0 0 100px #fff inset',
    },
  },
});


const LoginForm = ({ toggleRegistration, toggleTransition }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const search = queryString.parse(location.search);
    const [isAutoLogin, setIsAutoLogin] = useState(false);
    const [autologinMessage, setAutologinMessage] = useState('Please wait while auto-login is in progress.');
    const [loginProcess, setLoginProcess] = useState(false)
    const [studentData, setStudentData] = useState({});
    const firebaseContext = useFirebase()
    const {quotesData = []} = useQuotesData();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [show2FAModal, setShow2FAModal] = useState(false);
    const [pendingUser, setPendingUser] = useState(null);
    const [pendingRoute, setPendingRoute] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showPopupBlockedModal, setShowPopupBlockedModal] = useState(false);
    const googleAuthPopupTimeoutRef = useRef(null);
    const googleAuthPopupOpenedRef = useRef(false);
    const clearStoredKey = () => Object.keys(local_store_keys).forEach(key => localStorage.removeItem(key))
    useEffect(() => {
        const clearExistingSession = async () => {
            try {
                const existingUser = await getCurrentUser().catch(() => null);
                if (existingUser) {
                    SessionManager();
                    localStorage.removeItem('whats_new_shown');
                }
            } catch (error) {
            }
        };
        
        clearExistingSession();
        
        return () => {
            if (googleAuthPopupTimeoutRef.current) {
                clearTimeout(googleAuthPopupTimeoutRef.current);
            }
        };
    }, []);

    const handleCode = async (search_content) => {
        try {
            if(typeof search_content === 'object' && Object.keys(search_content).length > 0) {
            if (typeof search_content === "object" && search_content.code && search_content.response_type == "code" && typeof search_content.code === "string") {
               await handleClasslinkAuth({
                search: search_content,
                setIsAutoLogin,
                setError,
                loginWithSchoology,
                setLoading,
                setAutologinMessage,
                dispatch
               })
            }
            else if (typeof search_content === "object" && search_content.code && typeof search_content.code === "string") {
                const canvas_selected_auth_id = localStorage.getItem(local_store_keys.canvas_selected_district)
                let domain = null
                if (document.referrer) {
                    domain = new URL(document.referrer).hostname
                }
                if ((domain && !domain.includes("clever.com")) || canvas_selected_auth_id) {
                   const canvas_auth = await handleCanvasAuth({
                        search: search_content,
                        setIsAutoLogin,
                        setError,
                        setAutologinMessage,
                        setLoading,
                        loginWithSchoology,
                        dispatch
                    });
                    if(canvas_auth&&canvas_auth.not_a_canvas_auth){
                        await handleCleverAuth({
                            search: search_content,
                            setIsAutoLogin,
                            setError,
                            setAutologinMessage,
                            setLoading,
                            loginWithSchoology,
                            dispatch
                        });
                    }
                    return;
                }
                await handleCleverAuth({
                    search: search_content,
                    setIsAutoLogin,
                    setError,
                    setAutologinMessage,
                    setLoading,
                    loginWithSchoology,
                    dispatch
                });
            }
            else if (typeof search_content === "object" && search_content.custom_canvas_api_domain) {
                await handleCanvasAuth({
                    search: search_content,
                    setIsAutoLogin,
                    setError,
                    setAutologinMessage,
                    setLoading,
                    loginWithSchoology,
                    dispatch
                });
            }
            else if (typeof search_content === "object" && search_content.lti_message_type) {
                await handleCanvasAuth({
                    search: search_content,
                    setIsAutoLogin,
                    setError,
                    setAutologinMessage,
                    setLoading,
                    loginWithSchoology,
                    dispatch
                });
            }
            else if (typeof search_content === "object" && search_content.id_token) {
                await handleCanvasAuth({
                    search: search_content,
                    setIsAutoLogin,
                    setError,
                    setAutologinMessage,
                    setLoading,
                    loginWithSchoology,
                    dispatch
                });
            }
        }
        } catch (error) {
            Sentry.captureException(`591 Error in handleCode: ${error}`);
            console.log(error,"errorerror")
            setAutologinMessage('There was an error while processing the auto-login. Please try again or contact support for assistance.');
            setError('There was an error while processing the auto-login. Please try again or contact support for assistance.');
            setLoading(false);
            setIsAutoLogin(false);
        }
    }

    const loginWithSchoology = async (userData, redirect_path, redirectData) => {
        const { shouldUseDashboard, redirectPath } = await handleDeepLinkRedirect(dispatch, redirectData, redirect_path);
        
        let route = await dispatch(handleRedirect({}, userData, firebaseContext));
        clearStoredKey();
        if (route.path) {
            if (route.path == "/") {
                setLoading(false);
                setIsAutoLogin(false);
                setError(route.message)
            } else {
                toggleTransition();
                if (shouldUseDashboard) {
                    navigate(route.path, { replace: true })
                } else if (redirectPath) {
                    navigate(redirectPath, { replace: true })
                } else {
                    navigate(route.path, { replace: true })
                }
            }
        } else {
            setLoading(false);
            setIsAutoLogin(false);
            setError(route.message)
        }
    }

    const handleChange = (e) => {
        if (error) {
            setError(null);
        }
        setStudentData({
            ...studentData,
            [e.target.id]: e.target.value
        });
    };
    const handleRedirectItem = async (search_content) => {
        try {
            await getCurrentUser();
            let redircted = redirectToEbook(search_content)
            if (redircted) {
                return;
            }
        } catch (e) {
            Sentry.captureException(`597 Error in handleRedirectItem: ${e}`);
        }
    }
    const redirectToEbook = (search_content) => {
        let path = search_content.path
        if (path) {
            path = path.replace(/_/g, '&');
            path = `/${path}`

            if (path.includes("ebook?chapter")) {
                navigate(path)
                return true;
            }
            return false;
        }
        return false;
    }
    React.useEffect(() => {
        const search_content = queryString.parse(location.search);
        const host = window.location.hostname;
        // temprory redirect to integration test site
        // if (
        //     host !== 'dev.nosotrosonline.com' &&
        //     host !== 'localhost' && !search_content.key && search_content.code &&
        //     hasIntegrationParams(search_content)
        // ) {
        //     window.location.replace(`${INTEGRATION_TEST_SITE_URL}${INTEGRATION_TEST_SITE_PATH}${location.search}${location.hash || ''}`);
        //     return;
        // }
        if (typeof search_content === 'object' && Object.keys(search_content).length > 0) {
            if (search_content.path) {
                handleRedirectItem(search_content)
            } else if(search_content.key) {
                handleOnceLoginCall(search_content)
            }
            handleSchoologyAuth({
                search: search_content,
                setIsAutoLogin,
                setError,
                setAutologinMessage,
                setLoading,
                loginWithSchoology,
                dispatch,
                firebaseContext
            });
            handleCode(search_content)
        }
    }, [location])

 
    const handleOnceLoginCall = async (search_content) => {
        const result = await handleOnceLogin({
            key: search_content.key,
            setIsAutoLogin,
            setError,
            loginWithSchoology,
            dispatch
        });
        
        if (result && !result.success) {
            console.error('Once login failed:', result.error);
        }
    }
    //  -- Form Submit --------------------


    const completeLogin = async (route) => {
        var currentUrl = window.location.href;
        var urlSearchParams = new URLSearchParams(currentUrl);
        var hasClassId = currentUrl.includes('class_id');
        var hasGameId = currentUrl.includes('game_id');
        var hasSessionId = urlSearchParams.get('session_id');
        var classIdFromUrl = urlSearchParams.get('class_id');

        if (route.path == "/" || !route.path) {
            setError(route.message)
            setLoading(false);
            setIsAutoLogin(false);
        } else {
            toggleTransition();
            if (search && search.path) {
                let redircted = redirectToEbook()
                if (redircted) {
                    return;
                }
            }
            if (hasClassId && hasGameId && hasSessionId) {
                if ((hasClassId && hasGameId)) {
                    const getCurrectClass = await dispatch(handleGetClass(classIdFromUrl));
                    if (route?.user?.role === 'student' && getCurrectClass?.id === classIdFromUrl) {
                        setTimeout(() => navigate(`/student/class?sessionId=${hasSessionId}`), 700);
                    } else {
                        setError(`Access Denied  Contact your system adminstrator for help.`)
                        setLoading(false);
                        setIsAutoLogin(false);
                    }
                } else {
                    setError(`Having an issue with your scanned game  Contact your system adminstrator for help.`)
                    setLoading(false);
                    setIsAutoLogin(false);
                }
            } else {
                setTimeout(() => navigate(route.path), 700);
            }
        }
    };

    const handle2FAVerify = async () => {
        setShow2FAModal(false);
        setLoading(true);
        if (pendingRoute) {
            await completeLogin(pendingRoute);
        }
        setPendingUser(null);
        setPendingRoute(null);
    };

    const onSubmit = async (e, type) => {
        e.preventDefault();
        sessionStorage.clear(); 
        SessionManager();
        let authenticated;
        let route;
        setLoading(true);
        authenticated = await dispatch(handleLogin({ ...studentData, username: studentData.studentUsername.trim() }));
        if (authenticated) {
            route = await dispatch(handleRedirect(authenticated, null, firebaseContext));
            if (route.user && route.user.two_factor_enabled && route.user.email) {
                setPendingUser(route.user);
                setPendingRoute(route);
                setShow2FAModal(true);
                setLoading(false);
            } else {
                await completeLogin(route);
            }
        } else {
            setLoading(false); 
            setIsAutoLogin(false);
            setError('Invalid auth credentials, please try again.')
        }
    };

    //  -- Form Control -------------------

    const cotationRef = useRef(null)
    if (!cotationRef.current) {
        cotationRef.current = getRandomCotation(quotesData)
    }

    const responseGoogle = async (tokenResponse) => {
        googleAuthPopupOpenedRef.current = true;
        if (googleAuthPopupTimeoutRef.current) {
            clearTimeout(googleAuthPopupTimeoutRef.current);
            googleAuthPopupTimeoutRef.current = null;
        }
        await handleGoogleAuthResponse({
            tokenResponse,
            setLoginProcess,
            setAutologinMessage,
            setError,
            setLoading,
            setIsAutoLogin,
            loginWithSchoology,
            dispatch
        });
    };

    const handleGoogleAuthError = (error) => {
        googleAuthPopupOpenedRef.current = true;
        if (googleAuthPopupTimeoutRef.current) {
            clearTimeout(googleAuthPopupTimeoutRef.current);
            googleAuthPopupTimeoutRef.current = null;
        }
        
        if (error?.type === 'popup_blocked' || error?.error === 'popup_blocked' || error?.type === 'popup_failed_to_open') {
            setShowPopupBlockedModal(true);
            setLoading(false);
            setIsAutoLogin(false);
            setLoginProcess(false);
        } else if (error?.type === 'popup_closed' || error?.error === 'popup_closed_by_user') {
            setError('Login cancelled.');
            setLoading(false);
            setIsAutoLogin(false);
            setLoginProcess(false);
        } else {
            setError('Google authentication failed. Please try again.');
            setLoading(false);
            setIsAutoLogin(false);
            setLoginProcess(false);
        }
    };

    const handleSilentAuthError = (error) => {
        if (error?.type === 'popup_blocked' || error?.error === 'popup_blocked' || error?.type === 'popup_failed_to_open') {
            setShowPopupBlockedModal(true);
            setLoading(false);
            setIsAutoLogin(false);
            setLoginProcess(false);
        } else if (
            error?.error === 'interaction_required' ||
            error?.error_subtype === 'access_denied' ||
            error?.type === 'popup_closed' ||
            error?.error === 'popup_closed_by_user'
        ) {
            if (googleAuthPopupTimeoutRef.current) {
                clearTimeout(googleAuthPopupTimeoutRef.current);
                googleAuthPopupTimeoutRef.current = null;
            }
            googleAuthPopupOpenedRef.current = false;
            setTimeout(() => {
                googleLogin();
                googleAuthPopupTimeoutRef.current = setTimeout(() => {
                    if (!googleAuthPopupOpenedRef.current) {
                        setShowPopupBlockedModal(true);
                        setLoginProcess(false);
                        setLoading(false);
                        setIsAutoLogin(false);
                    }
                }, 5000);
            }, 100);
        } else {
            handleGoogleAuthError(error);
        }
    };

    const handlePopupBlockedContinue = () => {
        setShowPopupBlockedModal(false);
        setError(null);
        setLoginProcess(true);
        googleAuthPopupOpenedRef.current = false;
        if (googleAuthPopupTimeoutRef.current) {
            clearTimeout(googleAuthPopupTimeoutRef.current);
            googleAuthPopupTimeoutRef.current = null;
        }
        googleLogin();
        googleAuthPopupTimeoutRef.current = setTimeout(() => {
            if (!googleAuthPopupOpenedRef.current) {
                setShowPopupBlockedModal(true);
                setLoginProcess(false);
                setLoading(false);
                setIsAutoLogin(false);
            }
        }, 5000);
    };

    const { googleLoginSilent, googleLogin } = useGoogleAuth(
        responseGoogle, 
        handleSilentAuthError,
        handleGoogleAuthError
    );

    const placementExam = () => {
        navigate("/placement-exam")
    }

    return (
        <Wrapper>
            <StyledModal
                open={isAutoLogin}
            >
                <VideoContainer>
                    <CotationLoader cotation={cotationRef.current} messages={autologinMessage} />
                </VideoContainer>
            </StyledModal>
            <Container>
                <Toggle $active={true}
                    // onClick={toggleForm}
                >Student / Teacher</Toggle>
                {/* <Toggle active={formFlag} onClick={toggleForm}>Administrator</Toggle> */}
            </Container>
                    {error && (
                        <Row gutter={[0, 24]} style={{ width: '100%' }}>
                            <Col span={24}>
                                <Error>{error}</Error>
                            </Col>
                        </Row>
                    )}
                    <Description>If you do not remember your credentials, please get in contact with your administrator.</Description>
                    <Form onSubmit={e => onSubmit(e, 'student')} className='loginFormCstm'>
                        <Row gutter={[0, 24]} style={{ width: '100%' }}>
                            <Col span={24} className="floatingLabelBG">
                                <StyledTextField
                                    autoComplete="username"
                                    defaultValue={""}
                                    autoFocus
                                    id="studentUsername"
                                    label="Unique Username"
                                    onChange={handleChange}
                                    variant="outlined"
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 24]} style={{ width: '100%' }} className="addSpc">
                            <Col span={24} className="floatingLabelBG">
                                <StyledTextField
                                    autoComplete="current-password"
                                    id="password"
                                    label="Password"
                                    onChange={handleChange}
                                    variant="outlined"
                                    type={showPassword ? "text" : "password"}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                        size="small"
                                                        sx={{
                                                            padding: '4px',
                                                            backgroundColor: 'transparent',
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                            },
                                                        }}
                                                    >
                                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 24]} style={{ width: '100%' }}>
                            <Col span={24} className='webLoginBtn'>
                                <Button
                                    disabled={loading||(!studentData.studentUsername || studentData.studentUsername?.length < 2 || !studentData.password)}
                                    variant="contained"
                                    color="primary"
                                    style={{ width: '100%' }}
                                    type='submit'
                                >
                                    {loading ? <Loading size={20} white /> : 'Sign In'}
                                </Button>
                                {isAutoLogin && <AutoLoginMessage>{"Please Wait"}</AutoLoginMessage>}
                            </Col>
                        </Row>
                        <Row gutter={[0, 24]} style={{ width: '100%', marginTop: 16 }}>
                            <Col span={24}>
                                <CanvasLoginButton />
                            </Col>
                        </Row>
                        <Row gutter={[0, 24]} style={{ width: '100%', marginTop: 16 }}>
                            <Col span={24}>
                                <GoogleClassroomButton style={{ width: '100%' }} onClick={e => {
                                    e.preventDefault();
                                    setError(null);
                                    setLoginProcess(true);
                                    googleAuthPopupOpenedRef.current = false;
                                    
                                    if (googleAuthPopupTimeoutRef.current) {
                                        clearTimeout(googleAuthPopupTimeoutRef.current);
                                    }
                                    
                                    googleAuthPopupTimeoutRef.current = setTimeout(() => {
                                        if (!googleAuthPopupOpenedRef.current) {
                                            setShowPopupBlockedModal(true);
                                            setLoginProcess(false);
                                            setLoading(false);
                                            setIsAutoLogin(false);
                                        }
                                    }, 1500);
                                    
                                    if (getStoredGoogleEmail()) {
                                        googleLoginSilent();
                                    } else {
                                        googleLogin();
                                        if (googleAuthPopupTimeoutRef.current) {
                                            clearTimeout(googleAuthPopupTimeoutRef.current);
                                        }
                                        googleAuthPopupTimeoutRef.current = setTimeout(() => {
                                            if (!googleAuthPopupOpenedRef.current) {
                                                setShowPopupBlockedModal(true);
                                                setLoginProcess(false);
                                                setLoading(false);
                                                setIsAutoLogin(false);
                                            }
                                        }, 5000);
                                    }
                                }}>
                                    {loginProcess ? <Loading size={20} /> : "Google Classroom"}
                                </GoogleClassroomButton>
                            </Col>
                        </Row>
                        <Row gutter={[0, 24]} style={{ width: '100%', marginTop: 16 }}>
                            <Col span={24}>
                                <Button
                                    className='placementExamButton'
                                    variant="outlined"
                                    color="primary"
                                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    type='button'
                                    onClick={() => placementExam()}
                                >
                                    <Assignment style={{ fontSize: 18 }} />
                                    Placement Exam
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                    <RegisterContainer>
                        <Text>Need to register for a class?</Text>
                        <Link onClick={toggleRegistration}>Register Now</Link>
                    </RegisterContainer>
                    <TwoFactorAuthModal
                        visible={show2FAModal}
                        user={pendingUser}
                        onVerify={handle2FAVerify}
                        onCancel={() => {
                            setShow2FAModal(false);
                            setPendingUser(null);
                            setPendingRoute(null);
                            setError('Login cancelled. Please try again.');
                        }}
                    />
                    <Modal
                        open={showPopupBlockedModal}
                        onClose={() => setShowPopupBlockedModal(false)}
                        aria-labelledby="popup-blocked-modal-title"
                        aria-describedby="popup-blocked-modal-description"
                    >
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'white',
                            padding: '24px',
                            borderRadius: '8px',
                            maxWidth: '400px',
                            width: '90%',
                            outline: 'none'
                        }}>
                            <h2 id="popup-blocked-modal-title" style={{ marginTop: 0, marginBottom: '16px' }}>
                                Popup Blocked
                            </h2>
                            <p id="popup-blocked-modal-description" style={{ marginBottom: '24px', color: COLOR.black500 }}>
                                Popup is blocked by your browser. Please click here to continue login.
                            </p>
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                                <GoogleClassroomButton 
                                    style={{ width: '100%' }}
                                    onClick={handlePopupBlockedContinue}
                                >
                                    Google Classroom
                                </GoogleClassroomButton>
                            </div>
                        </div>
                    </Modal>
        </Wrapper>
    );
};


export default LoginForm;